import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Image as ImageIcon, Inbox, MessageSquare, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { listApplications, APP_STATUS, archiveApplication, restoreApplication, deleteApplication, bulkApplications, errMsg, formatPhone } from "@/lib/applyApi";
import { RowMenu, DeleteDialog } from "@/components/ApplicationActions";
import { TestModeBanner, TestBadge, setTestFlag } from "@/components/TestMode";
import { Checkbox } from "@/components/ui/checkbox";
import { MobileMenu, MobileTabBar } from "@/components/AppNav";
import { AppHeader } from "@/components/brand/AppHeader";
import { breedLabel, disciplineLabels, priceLabel } from "@/lib/horseOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AdStatusPill } from "@/components/AdTemplateStatus";
import { InvitationsPanel } from "@/components/invite/InvitationsPanel";

export function SourceBadge({ source, testId }) {
  const pre = source === "pre_approved_invitation";
  return <span data-testid={testId} className={`inline-flex border rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${pre ? "bg-violet-50 text-violet-800 border-violet-200" : "bg-white text-stone-500 border-stone-200"}`}>{pre ? "Pre-Approved Invitation" : "Regular Application"}</span>;
}

const ALL = "__all__";
const ARCHIVED = "__archived__";
const GRID = "lg:grid-cols-[28px_1.2fr_1.1fr_1.5fr_1fr_0.9fr_0.9fr_0.8fr_1.2fr_36px]";
const SORTS = [
  ["newest", "Newest first"], ["oldest", "Oldest first"], ["horse", "Horse name (A–Z)"],
  ["seller", "Seller name (A–Z)"], ["price", "Expected price (high–low)"],
];
const priceNum = (v) => (typeof v === "number" ? v : 0);
const fmt = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function AppProgress({ r, i }) {
  if (r.status !== "approved") return null;
  const signed = r.contract_status === "signed";
  return (
    <div data-testid={`app-progress-${i}`} className="mt-1 flex flex-wrap gap-1">
      <SourceBadge source={r.source} testId={`source-badge-${i}`} />
      <span data-testid={`contract-badge-${i}`} className={`inline-flex border rounded-full px-2 py-0.5 text-[11px] font-semibold ${signed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-50 text-stone-600 border-stone-200"}`}>
        {signed ? "Contract Signed" : r.contract_status === "not_started" ? "Contract Not Started" : "Awaiting Contract Signature"}
      </span>
      {r.ad_template_status === "waiting_on_seller"
        ? <AdStatusPill status="waiting_on_seller" testId={`ad-badge-${i}`} label="Waiting on Ad Template" />
        : <AdStatusPill status={r.ad_template_status} testId={`ad-badge-${i}`} prefix="Ad Template: " />}
    </div>
  );
}

export function StatusPill({ status }) {
  const [l, tone] = APP_STATUS[status] || APP_STATUS.draft;
  return <span data-testid="app-status" className={`inline-flex border rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${tone}`}>{l}</span>;
}

function sortRows(rows, sort) {
  const by = {
    newest: (a, b) => (b.submitted_at || b.created_at || "").localeCompare(a.submitted_at || a.created_at || ""),
    oldest: (a, b) => (a.submitted_at || a.created_at || "").localeCompare(b.submitted_at || b.created_at || ""),
    horse: (a, b) => (a.horse.horse_name || "~").localeCompare(b.horse.horse_name || "~"),
    seller: (a, b) => (a.contact.full_name || "~").localeCompare(b.contact.full_name || "~"),
    price: (a, b) => priceNum(b.horse.expected_price) - priceNum(a.horse.expected_price),
  };
  return [...rows].sort(by[sort]);
}

export default function Applications() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(ALL);
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(new Set());
  const [toDelete, setToDelete] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoading(true);
    setSelected(new Set());
    listApplications(status === "draft", status === ARCHIVED)
      .then(setRows)
      .catch(() => toast.error("Could not load applications"))
      .finally(() => setLoading(false));
  }, [status, reload]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const f = rows.filter((r) => (status === ALL || status === ARCHIVED || r.status === status) && (!t || [
      r.horse.horse_name, r.contact.full_name, r.contact.email, r.contact.phone, ...disciplineLabels(r.horse), breedLabel(r.horse),
    ].filter(Boolean).join(" ").toLowerCase().includes(t)));
    return sortRows(f, sort);
  }, [rows, q, status, sort]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShown = shown.length > 0 && shown.every((r) => selected.has(r.id));
  const selRows = shown.filter((r) => selected.has(r.id));
  const run = async (fn, okMsg) => {
    try {
      const res = await fn();
      (res?.skipped || []).forEach((s) => toast.error(`${s.horse_name || "Application"}: ${s.reason}`));
      toast.success(okMsg);
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
    } finally {
      setReload((n) => n + 1);
    }
  };
  const bulk = (action) => run(() => bulkApplications(action, selRows.map((r) => r.id)), `${selRows.length} application(s) ${action === "archive" ? "archived" : "restored"}`);
  const confirmDelete = () => run(
    () => (toDelete.length === 1 && !selected.size ? deleteApplication(toDelete[0].id) : bulkApplications("delete", toDelete.map((r) => r.id), "DELETE")),
    "Deleted permanently",
  ).then(() => setToDelete(null));
  const inArchive = status === ARCHIVED;

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-24 md:pb-10 overflow-x-clip">
      <AppHeader title="Applications" onBack={() => navigate("/")} backTestId="back-to-horses">
          <MobileMenu />
      </AppHeader>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-4"><InvitationsPanel /></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-4">
        <div className="bg-white border border-stone-200 rounded-xl p-3 flex flex-col lg:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input data-testid="applications-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search horse, seller, email, phone…" className="pl-9 h-11 lg:h-10 text-base lg:text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2 lg:w-[460px]">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="applications-status-filter" className="h-11 lg:h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All submitted</SelectItem>
                {Object.entries(APP_STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                <SelectItem value={ARCHIVED} data-testid="filter-archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="applications-sort" className="h-11 lg:h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 min-h-[36px]">
          <p data-testid="applications-count" className="text-xs text-stone-500 mr-auto">{loading ? "Loading…" : `${shown.length} ${inArchive ? "archived " : ""}application${shown.length === 1 ? "" : "s"}`}</p>
          {selRows.length > 0 && (
            <div data-testid="bulk-bar" className="flex flex-wrap items-center gap-2">
              <span data-testid="bulk-count" className="text-xs font-semibold text-stone-700">{selRows.length} selected</span>
              {inArchive ? (
                <Button data-testid="bulk-restore" size="sm" variant="outline" onClick={() => bulk("restore")} className="rounded-full"><ArchiveRestore className="h-4 w-4 mr-1" />Restore</Button>
              ) : (
                <Button data-testid="bulk-archive" size="sm" variant="outline" onClick={() => bulk("archive")} className="rounded-full"><Archive className="h-4 w-4 mr-1" />Archive</Button>
              )}
              <Button data-testid="bulk-delete" size="sm" variant="outline" onClick={() => setToDelete(selRows)} className="rounded-full border-red-300 text-red-600"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
            </div>
          )}
        </div>
      </div>

      <TestModeBanner />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 pt-3">
        {loading ? <Skeleton className="h-40 rounded-xl" /> : shown.length === 0 ? (
          <div className="text-center py-16 text-stone-500"><Inbox className="h-10 w-10 mx-auto mb-2 text-stone-300" />{inArchive ? "No archived applications." : "No applications yet."}</div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <div className={`hidden lg:grid ${GRID} gap-3 px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-stone-400 border-b border-stone-100 items-center`}>
              <Checkbox data-testid="select-all" aria-label="Select all shown applications" checked={allShown}
                onCheckedChange={(v) => setSelected(v ? new Set(shown.map((r) => r.id)) : new Set())} />
              <span>Horse</span><span>Seller</span><span>Email</span><span>Phone</span><span>Type</span><span>Price</span><span>Submitted</span><span>Status</span><span />
            </div>
            <label className="lg:hidden flex items-center gap-2 px-4 py-2 border-b border-stone-100 text-xs text-stone-500">
              <Checkbox data-testid="select-all-mobile" checked={allShown} onCheckedChange={(v) => setSelected(v ? new Set(shown.map((r) => r.id)) : new Set())} /> Select all shown
            </label>
            <div className="divide-y divide-stone-100">
              {shown.map((r, i) => (
                <div key={r.id} data-testid={`application-row-${i}`} role="link" tabIndex={0} onClick={() => navigate(`/applications/${r.id}`)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/applications/${r.id}`); } }}
                  aria-label={`Open application for ${r.horse.horse_name || "unnamed horse"}`}
                  className={`cursor-pointer hover:bg-stone-50 active:bg-stone-100 px-4 py-3 lg:grid ${GRID} lg:gap-3 lg:items-center text-sm ${selected.has(r.id) ? "bg-amber-50/60" : ""}`}>
                  <div className="hidden lg:block" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <Checkbox data-testid={`select-row-${i}`} aria-label={`Select ${r.horse.horse_name || "application"}`} checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} />
                  </div>
                  <div className="flex items-start justify-between gap-2 lg:block">
                    <div className="lg:hidden pt-0.5" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <Checkbox data-testid={`select-row-mobile-${i}`} aria-label={`Select ${r.horse.horse_name || "application"}`} checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-stone-900 truncate flex items-center gap-1.5">{r.is_test && <TestBadge testId={`row-test-badge-${i}`} />}<span className="truncate">{r.horse.horse_name || "Unnamed horse"}</span></div>
                      <div className="text-xs text-stone-400 flex items-center gap-1"><ImageIcon className="h-3 w-3" />{r.media_count} photo{r.media_count === 1 ? "" : "s"}
                        {r.unread_messages > 0 && (
                          <span data-testid={`unread-badge-${i}`} className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-800 text-white px-1.5 py-0.5 text-[10px] font-bold">
                            <MessageSquare className="h-3 w-3" />{r.unread_messages} new
                          </span>
                        )}
                      </div>
                      <AppProgress r={r} i={i} />
                    </div>
                    <div className="lg:hidden shrink-0 flex items-center gap-1"><StatusPill status={r.status} />
                      <RowMenu row={r} index={`m${i}`} onOpen={() => navigate(`/applications/${r.id}`)} onArchive={() => run(() => archiveApplication(r.id), "Application archived")}
                        onRestore={() => run(() => restoreApplication(r.id), "Application restored")} onDelete={() => { setSelected(new Set()); setToDelete([r]); }}
                        onToggleTest={() => run(() => setTestFlag(r.id, !r.is_test), r.is_test ? "TEST mark removed" : "Marked as TEST")} />
                    </div>
                  </div>
                  <div className="text-stone-700 truncate">{r.contact.full_name || "—"}</div>
                  <div className="text-stone-500 truncate text-xs lg:text-sm">{r.contact.email || "—"}</div>
                  <div className="text-stone-500 text-xs lg:text-sm">{formatPhone(r.contact.phone) || "—"}</div>
                  <div className="hidden lg:block text-stone-600 truncate" title={disciplineLabels(r.horse).join(", ")}>{disciplineLabels(r.horse).join(", ") || breedLabel(r.horse) || "—"}</div>
                  <div className="text-stone-600 text-xs lg:text-sm"><span className="lg:hidden text-stone-400">Price: </span><span data-testid={`application-price-${i}`}>{priceLabel(r.horse) || "—"}</span></div>
                  <div className="text-stone-500 text-xs lg:text-sm"><span className="lg:hidden text-stone-400">Submitted: </span>{fmt(r.submitted_at)}</div>
                  <div className="hidden lg:block"><StatusPill status={r.status} /></div>
                  <div className="hidden lg:block">
                    <RowMenu row={r} index={i} onOpen={() => navigate(`/applications/${r.id}`)} onArchive={() => run(() => archiveApplication(r.id), "Application archived")}
                      onRestore={() => run(() => restoreApplication(r.id), "Application restored")} onDelete={() => { setSelected(new Set()); setToDelete([r]); }}
                        onToggleTest={() => run(() => setTestFlag(r.id, !r.is_test), r.is_test ? "TEST mark removed" : "Marked as TEST")} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <DeleteDialog rows={toDelete} onCancel={() => setToDelete(null)} onConfirm={confirmDelete} />
      </main>
      <MobileTabBar />
    </div>
  );
}
